import { useQuery } from '@tanstack/react-query';
import { orpc } from '../../../rpc/client';
import type {
  ListNotesInput,
  SearchNotesInput,
  ImproveNoteInput,
  ImproveNoteResponse,
  ListNoteUsagesInput,
} from '@erp/rpc';

/**
 * Hook to list memory bank notes with filtering and pagination
 */
export function useMemoryBankNotes(input: Partial<ListNotesInput> = {}) {
  const queryInput: ListNotesInput = {
    limit: 20,
    offset: 0,
    ...input,
  };

  return useQuery(
    orpc.memoryBank.list.queryOptions({
      input: queryInput,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  );
}

/**
 * Hook to get a specific memory bank note by ID
 */
export function useMemoryBankNote(id: string) {
  return useQuery(
    orpc.memoryBank.get.queryOptions({
      input: { id },
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  );
}

/**
 * Hook to batch get multiple memory bank notes by IDs
 */
export function useMemoryBankNotesBatch(ids: string[]) {
  return useQuery(
    orpc.memoryBank.batchGet.queryOptions({
      input: { ids },
      enabled: ids.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }),
  );
}

/**
 * Hook to search memory bank notes with vector similarity
 */
export function useSearchMemoryBankNotes(input: SearchNotesInput) {
  return useQuery(
    orpc.memoryBank.search.queryOptions({
      input,
      enabled: !!input.query,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }),
  );
}

/**
 * Hook to get memory bank service statistics
 */
export function useMemoryBankStats() {
  return useQuery(
    orpc.memoryBank.stats.queryOptions({
      staleTime: 10 * 60 * 1000, // 10 minutes
    }),
  );
}

/**
 * Hook to get AI improvement suggestions for a memory bank note
 */
export function useGetImproveNoteSuggestion(
  input: ImproveNoteInput | null,
  { onSuccess }: { onSuccess?: (response: ImproveNoteResponse) => void } = {},
) {
  return useQuery(
    orpc.memoryBank.getImproveNoteSuggestion.queryOptions({
      input: input as ImproveNoteInput,
      enabled: !!input?.noteId,
      staleTime: 0, // Don't cache improvement suggestions
      gcTime: 0, // Don't keep in memory after unmount
      onSuccess,
    }),
  );
}

/**
 * Hook to list note usages for an entity
 */
export function useListNoteUsages(input: ListNoteUsagesInput | null) {
  return useQuery(
    orpc.memoryBank.listUsages.queryOptions({
      input: input as ListNoteUsagesInput,
      enabled: !!input?.entityId && !!input?.entityType,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }),
  );
}
